import React, { useContext } from "react"
import { Button, Col, Form, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { Formik } from "formik"

import {
  getFieldValues,
  getSectionFieldNames,
  getSettings,
} from "@/onboarding/features/product/selectors"
import { updateFields } from "@/onboarding/features/product/Slice"
import FormikBootstrap from "@/components/formik-bootstrap"
import { ReferencedValueTypes, ReservedFields } from "@/onboarding/features/product/models/Product"
import SubmittingSpinner from "@/onboarding/features/product/components/shared/SubmittingSpinner"
import { SubmittingContext } from "@/onboarding/features/product/helpers"
import FormikTextList from "@/onboarding/features/product/components/shared/FormikTextList"
import SubmitButton from "@/onboarding/features/product/components/shared/buttons/SubmitButton"
import TrackFormDirty from "@/onboarding/features/product/forms/TrackFormDirty"
import SubmitAndApplyButton from "@/onboarding/features/product/components/shared/buttons/SubmitAndApplyButton"
import CruiseLineOnly from "@/auth_descriptor/CruiseLineOnly"
import ReferencedValueSelect from "@/onboarding/features/product/components/shared/ReferencedValueSelect"

const Edit = ({ setEditing }): JSX.Element => {
  const dispatch = useDispatch()
  const fieldNames = useSelector(getSectionFieldNames.description)
  const initialValues = useSelector(getFieldValues(...fieldNames))
  const fieldsSettings = useSelector(getSettings(...fieldNames))
  const [submitting, setSubmitting] = useContext(SubmittingContext)

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => {
        setSubmitting(true)
        dispatch(updateFields({ values }))
      }}
    >
      {({ handleSubmit }) => (
        <Form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
          <TrackFormDirty formId="webContent" />
          <FormikBootstrap.TextInput
            name={ReservedFields.WEBSITE_TOUR_NAME}
            label="Website Tour Name"
          />
          <FormikBootstrap.TextAreaInput
            rows={5}
            name={ReservedFields.OVERVIEW}
            label="Overview (Romance Copy)"
            helpText="Describe the tour in language suitable for display to guests interested in purchasing the tour"
            readOnly={!fieldsSettings[ReservedFields.OVERVIEW].writeable}
            showLockedIcon={!fieldsSettings[ReservedFields.OVERVIEW].writeable}
            showRequired={fieldsSettings[ReservedFields.OVERVIEW].required}
          />
          <CruiseLineOnly>
            <Col md={3}>
              <ReferencedValueSelect
                name={ReservedFields.WEBSITE_CANCELLATION_POLICY}
                type={ReferencedValueTypes.WEBSITE_CANCELLATION_POLICY}
                label="Website Cancellation Policy"
              />
            </Col>
          </CruiseLineOnly>
          <Row>
            <Col md={6}>
              <FormikTextList
                name={ReservedFields.HIGHLIGHTS}
                label="Highlights"
                readOnly={!fieldsSettings[ReservedFields.HIGHLIGHTS].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.HIGHLIGHTS].writeable}
                showRequired={fieldsSettings[ReservedFields.HIGHLIGHTS].required}
              />
            </Col>
            <Col md={6}>
              <FormikTextList
                name={ReservedFields.IMPORTANT_NOTES}
                label="Important Notes"
                readOnly={!fieldsSettings[ReservedFields.IMPORTANT_NOTES].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.IMPORTANT_NOTES].writeable}
                showRequired={fieldsSettings[ReservedFields.IMPORTANT_NOTES].required}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormikTextList
                name={ReservedFields.WHAT_TO_BRING}
                label="What to Bring"
                readOnly={!fieldsSettings[ReservedFields.WHAT_TO_BRING].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.WHAT_TO_BRING].writeable}
                showRequired={fieldsSettings[ReservedFields.WHAT_TO_BRING].required}
              />
            </Col>
            <Col md={6}>
              <FormikTextList
                name={ReservedFields.WHAT_TO_WEAR}
                label="What to Wear"
                readOnly={!fieldsSettings[ReservedFields.WHAT_TO_WEAR].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.WHAT_TO_WEAR].writeable}
                showRequired={fieldsSettings[ReservedFields.WHAT_TO_WEAR].required}
              />
            </Col>
          </Row>
          <div className="d-flex gap-2">
            <SubmitButton />
            <SubmitAndApplyButton />
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => setEditing(false)}
              disabled={submitting}
            >
              <i className="fas fa-times me-2" />
              Cancel
            </Button>
            <SubmittingSpinner />
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default Edit
